/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { Container } from './styles';
import Select, { IOption } from '~/components/Select';
import BtnNewEvent from '~/components/BtnNewEvent';
import { useResize } from '~/hooks/Resize';
import api from '~/services/api';
import DashboardTickets from '~/components/DashboardTickets';
import DashboardSalesChart from '~/components/DashboardSalesChart';
import DashboardKitsSold from '~/components/DashboardKitsSold';
import DashboardModCatTable from '~/components/DashboardModCatTable';
import Loading from '~/components/Loading';

interface IDashboardEventItem {
  event_id: number;
  organizer_id: number;
  organizer_name: string;
  event_name: string;
  subscriptions: number;
  revenue: string;
  goal_average_ticket: string;
  sold_tickets: string;
  total_tickets: string;
  total_org: string;
  avg_ticket_sold: string;
  goal_tickets_pct: string;
  goal_revenue_pct: string;
}

const Dashboard: React.FC = () => {
  const { width } = useResize();
  const [lineChart, setLineChart] = useState(450);
  const [metrics, setMetrics] = useState({
    ticketsSold: 0,
    revenue: 0,
    averageTicket: 0,
  });
  const [metricsTarget, setMetricsTarget] = useState({
    registrations: 0,
    revenue: 0,
    averageTicket: 0,
    ticketTarget: 0,
    revenueTarget: 0,
  });
  const [eventId, setEventId] = useState<number | undefined>(-2);
  const formRefEvent = useRef<FormHandles>(null);
  const [eventsModal, setEventsModal] = useState<IOption[]>([]);
  const [, setOrganizerId] = useState<number | null>(null);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(false);
  const [expand, setExpand] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // subir

  const handleLoadDashboard = useCallback(async (selectedEventId?: number) => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (selectedEventId) {
        params.event_id = selectedEventId;

        const response = await api.get<IDashboardEventItem[]>(
          '/sales/organizers/dashboard-event',
          { params }
        );

        const allData = response.data;

        let ticketsSold = 0;
        let revenue = 0;
        let averageTicket = 0;
        let registrations = 0;
        let targetRevenue = 0;
        let targetAverageTicket = 0;
        let ticketTarget = 0;
        let revenueTarget = 0;

        if (allData[0]) {
          ticketsSold = parseInt(allData[0].total_tickets, 10) || 0;
          revenue = parseFloat(allData[0].total_org) || 0;

          registrations = allData[0].subscriptions || 0;
          targetRevenue = parseFloat(allData[0].revenue) || 0;

          revenueTarget = parseFloat(allData[0].goal_revenue_pct) || 0;
          ticketTarget = parseFloat(allData[0].goal_tickets_pct) || 0;

          averageTicket = parseFloat(allData[0].avg_ticket_sold) || 0;
          targetAverageTicket = parseFloat(allData[0].goal_average_ticket) || 0;
        }

        setMetrics({ ticketsSold, revenue, averageTicket });

        setMetricsTarget({
          registrations,
          revenue: targetRevenue,
          averageTicket: targetAverageTicket,
          ticketTarget,
          revenueTarget,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (width > 1200) {
      setLineChart(357);
    }
    if (width > 1400) {
      setLineChart(400);
    }
    if (width > 1600) {
      setLineChart(400);
    }
    if (width > 1700) {
      setLineChart(440);
    }
  }, [width]);

  const handleChangeTextEvent = useCallback((text) => {
    const value = text === 'Todos' ? '' : text;
    setEventName(value);
    setSearchTerm(value);
  }, []);

  const handleSelectEvent = useCallback(
    (event: IOption) => {
      const isAll = event.value === 'Todos';

      if (isAll) {
        setExpand(true);
      } else {
        setExpand(false);
      }

      setEventId(isAll ? undefined : Number(event.id));

      setEventName(isAll ? '' : event.value);

      handleLoadDashboard(isAll ? undefined : Number(event.id));
    },
    [handleLoadDashboard]
  );

  const handleLoadEventsModal = useCallback(async (search = '') => {
    try {
      const response = await api.get('events/organizer/dropdown', {
        params: {
          search: search.trim(),
          status_ids: [3, 4, 5, 6, 7, 8, 9],
        },
      });
      const data = response.data
        .map((event: any) => ({
          id: event?.id,
          value: `${event?.name} (${event?.id})`,
          selected: false,
        }))
        .sort((a: IOption, b: IOption) => a.value.localeCompare(b.value));
      setOrganizerId(response.data[0]?.organizer_id);

      setEventsModal([
        {
          id: '',
          value: 'Todos',
          selected: true,
        } as IOption,
        ...data,
      ]);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    handleLoadEventsModal(debouncedSearch);
  }, [debouncedSearch, handleLoadEventsModal]);

  useEffect(() => {
    handleLoadDashboard();
  }, [handleLoadDashboard]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleClickExpand = useCallback(() => {
    if (eventId && eventId > 0) {
      setExpand(!expand);
    } else {
      setExpand(true);
    }
  }, [expand, eventId]);

  return (
    <Container className="pb-4">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 px-0">
            <div className="zoom bg-white d-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Dashboard</h1>
              <Form
                ref={formRefEvent}
                onSubmit={() => console.log('submit')}
                className="w-50"
              >
                <div className="d-flex justify-content-center align-items-center">
                  <h2 className="h5 mb-0 text-end w-50 me-3">
                    Filtre por evento
                  </h2>
                  <Select
                    name="event"
                    options={eventsModal}
                    placeholder="Selecione o evento"
                    onChangeText={handleChangeTextEvent}
                    value={eventName}
                    onChange={(e: any) => handleSelectEvent(e)}
                    className="bg-white rounded"
                  />
                </div>
              </Form>
              <BtnNewEvent />
            </div>
          </div>

          <DashboardTickets
            expand={expand}
            metrics={metrics}
            metricsTarget={metricsTarget}
          />
          <div className={`${expand ? 'col-lg-12' : 'col-lg-6'}`}>
            <div className="row">
              <DashboardSalesChart
                expand={expand}
                onToggleExpand={handleClickExpand}
                height={lineChart}
                eventId={eventId === -2 ? undefined : eventId}
              />
            </div>
          </div>
          <DashboardKitsSold selectedEventId={eventId} />
          <DashboardModCatTable selectedEventId={eventId} />
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Dashboard;
